<template>
  <button
    type="button"
    class="base-button"
    :disabled="disabled"
    :class="classes"
    :style="{
      'background' : type === 'solid' ? bgColor : '#FFFFFF',
      'border': borderColor ? `1px solid ${borderColor}` : null
    }"
    @click="onClick"
  >
    <div v-if="skeletonLoading" class="base-button__skeleton"></div>
    <slot/>
    <BasePreloader
      v-if="loading"
      size="s"
      :color="type === 'solid' ? 'white' : 'purple'"
    />
    <BaseFeatureLocker
      v-if="locked"
      :tooltip-text="lockerText"
      :size="'s'"
    />
  </button>
</template>

<script>

import BaseFeatureLocker from '@/views/RenovaDashboard/components/ui-kit/Cards/BaseFeatureLocker';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import skeleton from 'element-ui/packages/skeleton';

export default {
  name: 'BaseButton',
  components: {
    BaseFeatureLocker,
    BasePreloader,
  },
  props: {
    type: {
      type: String,
      default: 'solid',
      required: false,
      validate: (value) => ['solid', 'outline', 'icon', 'error'].includes(value),
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    isSmall: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    skeletonLoading: {
      type: Boolean,
      default: false,
    },
    // Declared explicitly: this used to be an undeclared attribute that fell through onto
    // the native <button>, so a disabled button emitted no click at all and callers had no
    // way to tell a dead button from a broken one. Always pair it with a visible reason.
    disabled: {
      type: Boolean,
      default: false,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    lockerText: {
      type: String,
      default: null,
    },
    bgColor: {
      type: String,
    },
    borderColor: {
      type: String,
    },
  },
  computed: {
    classes() {
      return {
        'base-button--outline': this.type === 'outline',
        'base-button--icon': this.type === 'icon',
        'base-button--error': this.type === 'error',
        'base-button--fullWidth': this.fullWidth,
        'base-button--isSmall': this.isSmall,
        'base-button--locked': this.locked,
        'base-button--disabled': this.disabled,
      };
    },
  },
  methods: {
    onClick() {
      if (!this.locked && !this.disabled) {
        this.$emit('click');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.base-button {
  font-family: 'Inter', serif;
  display: inline-flex;
  align-items: center;
  position: relative;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  padding: 12px 16px;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  cursor: pointer;
  color: #FFFFFF;
  background: var(--solid-purple);
  border: 1px solid rgba(0, 0, 0, 0.1);
  transition: .3s;
  overflow: hidden;
  white-space: nowrap;
  justify-content: center;

  &--outline {
    color: var(--solid-purple);
    background: #FFFFFF;
    border: 1px solid var(--solid-purple);
    font-weight: 400;
  }
  &--error {
    color: var(--solid-red);
    background: #FFFFFF;
    border: 1px solid var(--solid-red);
    font-weight: 400;
  }

  &--icon {
    color: var(--solid-purple);
    background: #FFFFFF;
    border: 1px solid var(--solid-purple);
    padding: 12px;
  }

  // not-allowed rather than default: opacity alone reads as a styling quirk, and users
  // kept clicking a dead button dozens of times without ever getting a cue.
  &:disabled,
  &--disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &--fullWidth {
    width: 100%;
    justify-content: center;
  }
  &--isSmall {
    padding: 6px 12px;
    border-radius: 4px;
  }
  &--locked {
    opacity: 0.4;
    cursor: default;
  }
  &__skeleton {
    background: transparent;
    overflow: hidden;
    position: absolute;
    z-index: 1;
    width: 100%;
    left: 0;
    height: 100%;
    box-shadow: 0 0 12px 4px var(--solid-purple-04);

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: -100%;
      height: 100%;
      width: 65px;
      background: linear-gradient(
          to right,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0.4) 50%,
          rgba(255, 255, 255, 0) 100%
      );
      animation: skeleton-loading 2s infinite;
    }
  }
}
@keyframes skeleton-loading {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}
</style>
<style lang="scss">
.base-button--locked {
  .feature-locker {
    background-color: transparent;
  }
}
</style>
